import { type FC, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil, noop, values } from 'lodash';
// Import Types
import type { CJVisualizationArgumentValue, CJVisualizationProgressUIModel } from 'types';
// Import UI Components
import { Dropdown } from '@geneui/components';
// Import Components
import CustomerJourneyVisualizationReferenceValue, {
    type CJVisualizationReferenceValueUpdate,
} from './CustomerJourneyVisualizationReferenceValue';
import CustomerJourneyVisualizationTemplateGroup from './CustomerJourneyVisualizationTemplateGroup';
import CustomerJourneyVisualizationToggleSection from './CustomerJourneyVisualizationToggleSection';
// Import Helpers
import { itemsIsValid } from 'services/Helpers';
import {
    getCJVisualizationProgressFormConfig,
    getCJVisualizationProgressOptions,
    getMatchedProperties,
    mapPropertiesToCascadeDropdownModel,
} from 'services/customerJourney/helpers';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { CJVisualizationProgressTypeEnum } from 'enums';
// Import Validations
import { CJVisualizationProgressTitleValidationSchema } from 'validators/schemas.validation';
// Import Styles
import styles from './customerJourneyVisualizationProgress.module.scss';

interface Props {
    defaultValue?: Nilable<CJVisualizationProgressUIModel>;
    node?: Nullable<AnyObject>;
    elements?: Nullable<AnyObject[]>;
    option?: Nullable<AnyObject>;
    collectedProperties?: AnyObject[];
    getUpdate?: (data: Partial<CJVisualizationProgressUIModel>) => void;
}

const progressTypeLabels = {
    [CJVisualizationProgressTypeEnum.Amount]: l.Amount,
    [CJVisualizationProgressTypeEnum.Count]: l.Count,
};

const getInitialValueState = (value: Nilable<CJVisualizationArgumentValue>): CJVisualizationReferenceValueUpdate => ({
    value: value ?? null,
    isValid: false,
});

const CustomerJourneyVisualizationProgress: FC<Props> = (props) => {
    const {
        defaultValue = null,
        node = null,
        elements = null,
        option = null,
        collectedProperties = [],
        getUpdate = noop,
    } = props;

    const { t } = useTranslation();

    const [showProgress, setShowProgress] = useState<boolean>(defaultValue?.showProgress === true);
    const [templateData, setTemplateData] = useState<AnyObject>(defaultValue?.templates ?? {});
    const [type, setType] = useState<Nullable<string>>(defaultValue?.type ?? null);
    const [current, setCurrent] = useState<CJVisualizationReferenceValueUpdate>(
        getInitialValueState(defaultValue?.current),
    );
    const [goal, setGoal] = useState<CJVisualizationReferenceValueUpdate>(getInitialValueState(defaultValue?.goal));

    // optionType of the config is the progress type, so references are filtered by WfMoney / WfCount
    const formConfig = useMemo(() => getCJVisualizationProgressFormConfig(type), [type]);

    const typesData = useMemo(
        () =>
            [CJVisualizationProgressTypeEnum.Amount, CJVisualizationProgressTypeEnum.Count].map((item) => ({
                value: item,
                label: t(progressTypeLabels[item]),
            })),
        [t],
    );

    const { currentOption, goalOption } = useMemo(
        () => getCJVisualizationProgressOptions(option, formConfig),
        [option, formConfig],
    );

    const referenceProperties = useMemo(
        () =>
            isNil(formConfig)
                ? []
                : mapPropertiesToCascadeDropdownModel(getMatchedProperties(formConfig.optionType, collectedProperties)),
        [formConfig, collectedProperties],
    );

    const templateChangeHandler = (languageKey, data) => {
        setTemplateData((prev) => {
            return { ...prev, [languageKey]: { ...prev?.[languageKey], ...data } };
        });
    };

    const typeChangeHandler = (item) => {
        setType(item?.value ?? null);
        setCurrent(getInitialValueState(null));
        setGoal(getInitialValueState(null));
    };

    useEffect(() => {
        let isValid = true;
        if (showProgress) {
            isValid = itemsIsValid(values(templateData)) && !isNil(type) && current.isValid && goal.isValid;
        }
        getUpdate({ showProgress, templates: templateData, type, current: current.value, goal: goal.value, isValid });
        // DEPS: [getUpdate]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showProgress, templateData, type, current, goal]);

    const renderValues = () => {
        if (isNil(formConfig) || isNil(node)) {
            return null;
        }

        return (
            <>
                <CustomerJourneyVisualizationReferenceValue
                    key={`current_${type}`}
                    label={t(l.Current)}
                    formConfig={formConfig}
                    option={currentOption}
                    value={current.value}
                    referenceProperties={referenceProperties}
                    node={node}
                    elements={elements}
                    isReferenceOnly={true}
                    hasSection={false}
                    getUpdate={setCurrent}
                />
                <div className={styles.progressGoal}>
                    <CustomerJourneyVisualizationReferenceValue
                        key={`goal_${type}`}
                        label={t(l.Goal)}
                        formConfig={formConfig}
                        option={goalOption}
                        value={goal.value}
                        referenceProperties={referenceProperties}
                        node={node}
                        elements={elements}
                        getUpdate={setGoal}
                    />
                </div>
            </>
        );
    };

    return (
        <CustomerJourneyVisualizationToggleSection
            label={t(l.Progress)}
            description={t(l.ProgressDescription)}
            isChecked={showProgress}
            onChange={setShowProgress}
        >
            <CustomerJourneyVisualizationTemplateGroup
                templates={templateData}
                onChange={templateChangeHandler}
                withDescription={false}
                namePlaceholder={l.Title}
                nameValidationSchema={CJVisualizationProgressTitleValidationSchema}
            />
            <div className={styles.progressFields}>
                <Dropdown
                    appearance="outline"
                    inputSize="default"
                    label={t(l.Type)}
                    labelAppearance="swap"
                    placeholder={t(l.Type)}
                    value={type}
                    data={typesData}
                    onChange={typeChangeHandler}
                    isValid={!isNil(type)}
                    errorText={t(l.CannotBeEmpty, { fieldName: t(l.Type) })}
                />
                {renderValues()}
            </div>
        </CustomerJourneyVisualizationToggleSection>
    );
};

CustomerJourneyVisualizationProgress.displayName = 'CustomerJourneyVisualizationProgress';

export default memo(CustomerJourneyVisualizationProgress);
